/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Automatically refreshes Firebase tokens and syncs with backend.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [firebaseToken, setFirebaseToken] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Sign up new user
  async function signup(email, password, displayName) {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Send verification email
      await sendEmailVerification(userCredential.user);

      // Get token
      const token = await userCredential.user.getIdToken();

      // Register user in backend
      const response = await axios.post(
        `${API_BASE_URL}/users/register`,
        {
          email: email,
          display_name: displayName
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setUserProfile(response.data);
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign in existing user
  async function login(email, password) {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Sign out
  async function logout() {
    try {
      setError(null);
      await signOut(auth);
      setUserProfile(null);
      setFirebaseToken(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Get user profile from backend
  const fetchUserProfile = useCallback(async (token) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/users/me`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setUserProfile(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      // If user not registered in backend, they need to call /register
      if (err.response?.status === 404) {
        console.log('User not registered in backend yet');
      }
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update user profile
  async function updateProfile(updates) {
    try {
      setError(null);
      const token = await currentUser.getIdToken();

      const response = await axios.put(
        `${API_BASE_URL}/users/me`,
        updates,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setUserProfile(response.data);
      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Get current Firebase token (useful for API calls)
  async function getToken() {
    if (currentUser) {
      return await currentUser.getIdToken();
    }
    return null;
  }

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Get and store token
        const token = await user.getIdToken();
        setFirebaseToken(token);

        // Fetch user profile from backend
        await fetchUserProfile(token);

        // Set up token refresh (every 55 minutes, tokens expire after 1 hour)
        const refreshInterval = setInterval(async () => {
          try {
            const newToken = await user.getIdToken(true); // Force refresh
            setFirebaseToken(newToken);
            console.log('Token refreshed');
          } catch (err) {
            console.error('Token refresh failed:', err);
          }
        }, 55 * 60 * 1000);

        setLoading(false);

        return () => clearInterval(refreshInterval);
      } else {
        setFirebaseToken(null);
        setUserProfile(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, [fetchUserProfile]);

  const value = {
    currentUser,
    firebaseToken,
    userProfile,
    error,
    signup,
    login,
    logout,
    updateProfile,
    getToken,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
